// BCM-898 · Goods Receipt Dashboard — digitalized-document review, embedded.
//
// Renders the real CommonDigitizedDocument — the same field-review + PDF/Excel
// preview UI used everywhere else in the app (confidence-driven field editing,
// Show Unedited Version, field history, Mark Review as Complete / Update &
// Re-run, consumption "Add New Consumed Batch/Material") — directly inside the
// Pending Actions section. Three things are deliberately different here vs.
// every other caller:
//   * No <Dialog>/<DialogTitle> wrapper — there is nothing to pop over, this is
//     already embedded in the expanded row.
//   * hideHeader — no Back button / "Digitalized <type>" strip; there is no
//     dialog to go back out of, and the tab bar below already names the document.
//   * hideProductRibbon — the PO/CMO/product-code strip is skipped since that
//     context already sits on this page.
// Everything else — including which fields are editable, which action button
// shows, and the mark-review payload — is CommonDigitizedDocument's own logic,
// unchanged.
//
// Document tabs (Certificate of Manufacture / Consumption Details, …) are still
// owned here: one CommonDigitizedDocument instance is mounted for the active
// tab at a time, switched by schrockenCustomFileID/digitizedData/initialData.
import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import CommonDigitizedDocument from "../../../Digitisation/CommonDigitizedDocument";
import { useGRStyles } from "../../goodsReceiptStyles";
import { useGRFeedback } from "../../context/GRFeedbackContext";
import TabsBar from "./TabsBar";
import { buildReviewDocs } from "../../utils/reviewDocs";

const GRDigitalizedDocumentReview = ({
  row,
  detail,
  onRefresh,
  onlyConsumption = false,
  // Optional DOC_CATEGORY value (see goodsReceipt/utils/constants) — when set,
  // only documents tagged with this documentCategoryType become tabs. Used by
  // PA2 so "all post-manufacturing documents" is exactly what shows, even if
  // digitizedExtractedData also carries other categories for this batch.
  category,
  // Optional exact document_type value (e.g. "CertificateOfManufacture") —
  // when set, only that single document becomes a tab. Used by the Produced
  // Qty Variance section (PA6), which should only ever show the Certificate
  // of Manufacture — the document the produced quantity is digitalized
  // from — instead of every document received for the batch.
  onlyDocType,
  // Either a node, or a function of the active tab's doc info (`{ docType, ... }`)
  // — used by PA2 so Resubmit Document can pre-target whichever tab is open.
  headRight,
  viewOnly,
}) => {
  const classes = useGRStyles();

  // ---- enumerate document tabs ------------------------------------------
  // Shared with BatchRow's "N Low Confidence Fields" label (utils/reviewDocs), so
  // that label is always the sum of these tabs' badges.
  const docs = useMemo(
    () => buildReviewDocs(detail, { onlyConsumption, category, onlyDocType }),
    [detail, onlyConsumption, category, onlyDocType]
  );

  const [activeIdx, setActiveIdx] = useState(0);
  // Default to the first tab with a non-zero review count (mockup rule). When no
  // tab has low-confidence fields left, open the first tab still waiting for
  // Mark Review as Complete instead.
  useEffect(() => {
    let firstToReview = docs.findIndex((d) => d.count > 0);
    if (firstToReview < 0) firstToReview = docs.findIndex((d) => d.needsMarkReview);
    setActiveIdx(firstToReview >= 0 ? firstToReview : 0);
  }, [docs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = docs[Math.min(activeIdx, Math.max(docs.length - 1, 0))];
  const feedback = useGRFeedback();

  // CommonDigitizedDocument (shared with Batch Execution) reports the outcome of
  // Mark Review as Complete / Update & Re-Run through onReviewActionResult — a prop
  // ONLY Goods Receipt passes, so Batch Execution keeps its own success dialog and
  // is completely unaffected. Here: success → our blocking toast + a status-change
  // poll; failure → the generic error toast. onDataRefresh is a no-op — the refetch
  // is driven by the toast handler, and a refresh here would refetch the table and
  // remount this embed (dropping in-place state).
  const noopRefresh = () => {};
  const handleReviewResult = ({ success, resetWorkflow }) => {
    if (!success) {
      feedback.notifyError();
      return;
    }
    // Update & Re-Run always resets/advances the batch. A per-document mark-review
    // may or may not advance it (e.g. other documents still pending review), so it
    // always polls with mayKeepStatus — the poll settles either way.
    feedback.notifySuccess({
      message: resetWorkflow ? "Workflow reset successfully." : "Review marked as complete.",
      statusChanging: true,
      batchId: row && row.schrockenDeliveryScheduleBatchID,
      prevStatus: row && row.SASLBatchStatusID,
      refetch: onRefresh,
      // Update & Re-Run restarts the workflow; a plain mark-review advances it a step.
      resetsBatch: !!resetWorkflow,
      // A plain mark-review may leave the status unchanged — settles after 4
      // unchanged reads, or waits for a steady status if it does change.
      mayKeepStatus: !resetWorkflow,
    });
  };

  // Field save / delete succeeded but the document isn't marked reviewed yet:
  // refresh the batch right away (refetchNow) so the review counts and every
  // tab's data are current. `localOnly` = an unsaved new row was removed with no
  // API call — nothing was updated, and a refresh would drop any other unsaved
  // rows.
  const handleFieldSaveResult = ({ message, localOnly }) => {
    feedback.notifySuccess({ message, refetch: localOnly ? null : onRefresh, refetchNow: true });
  };

  if (!detail) {
    return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={22} /></Box>;
  }
  if (docs.length === 0) {
    return (
      <Typography variant="body2" style={{ color: "#777", padding: "0.5rem 0" }}>
        No digitalized documents are available for this batch yet.
      </Typography>
    );
  }

  // BCM-898 bug fix: the tab strip (and each tab's review-count badge, rendered
  // inside the same docs.map below) used to require MORE than one document —
  // a document type with exactly one document got neither the tab nor its
  // notification count. docs.length is always >= 1 here (the ===0 case returns
  // above), so this now always shows the strip; multi-document behavior is
  // unchanged.
  const showInnerTabs = docs.length >= 1;
  const rawHeadRight = typeof headRight === "function" ? headRight(active) : headRight;
  // Sync/Resubmit are actions, not navigation, so they stay locked in view-only
  // mode even though the tabs beside them do not (BCM-898 follow-up).
  const resolvedHeadRight =
    rawHeadRight && viewOnly ? (
      <div style={{ opacity: 0.75, pointerEvents: "none" }}>{rawHeadRight}</div>
    ) : (
      rawHeadRight
    );

  return (
    <div>
      {viewOnly && (
        <Typography variant="caption" className={classes.viewOnlyNote}>
          View only — you do not have access to perform this action.
        </Typography>
      )}
      {/* The tab bar stays clickable in view-only mode — switching documents is
          just navigation, not the restricted action (BCM-898 follow-up). Only
          the document body below (where Mark Review as Complete / Update &
          Re-Run live) is locked down. */}
      {(showInnerTabs || resolvedHeadRight) && (
        <TabsBar headRight={resolvedHeadRight}>
          {showInnerTabs && docs.map((d, i) => (
            <div
              key={d.cfid}
              className={`${classes.tab} ${i === activeIdx ? classes.tabActive : ""}`}
              onClick={() => setActiveIdx(i)}
              role="tab"
              aria-selected={i === activeIdx}
              tabIndex={0}
            >
              {(d.docType || "Document").replace(/\bOf\b/g, "of")}
              {d.count > 0 && <span className={classes.tabBadge}>{d.count}</span>}
            </div>
          ))}
        </TabsBar>
      )}

      {active && (
        <div style={viewOnly ? { opacity: 0.75, pointerEvents: "none" } : undefined}>
          <CommonDigitizedDocument
            key={active.cfid}
            initialData={active.kvp}
            // digitizedData is read for exactly one thing — `documentType` — so pass
            // the raw record with the resolved type folded in, keeping docType out of
            // its "Unknown" fallback (which would otherwise be sent as the payload's
            // documentType and break every kvpJsonOriginal.document_type lookup).
            digitizedData={{ ...active.doc, documentType: active.docType }}
            batchDetails={detail}
            schrockenCustomFileID={active.cfid}
            poNumber={row?.scheduleAgreementNumber}
            batchNumber={row?.vendorBatchNumber}
            cmoBatchNumber={detail?.cmoBatchNumber || row?.cmoBatchNumber}
            // GR owns success/failure via onReviewActionResult (below); onClose and
            // onDataRefresh are no-ops here (no dialog to close, no in-place refresh).
            // Field saves refresh the batch via handleFieldSaveResult.
            onClose={noopRefresh}
            onDataRefresh={noopRefresh}
            onReviewActionResult={handleReviewResult}
            onFieldSaveResult={handleFieldSaveResult}
            hideHeader
            hideProductRibbon
          />
        </div>
      )}
    </div>
  );
};

export default GRDigitalizedDocumentReview;
